import React, { useEffect, useState } from 'react';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import InitialDetails from './AssistantForms/InitialDetails';
import TranscriberAndAudio from './AssistantForms/TranscriberAndAudio';
import AdditionalConfigurations from './AssistantForms/AdditionalConfigurations';
import NewActions from './AssistantForms/NewAction';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircleOutline,
  DashboardOutlined,
  DescriptionOutlined,
  RecordVoiceOverOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import api from "../api";
import NewPrompt from './AssistantForms/NewPrompt';
import { PublishButtonSx,TabIconSx } from '../styles/GlobalStyles';
import TooltipHover from './TooltipHover';

const tabsConfig = {
  Information: { label: 'Information', icon: <DashboardOutlined sx={TabIconSx} /> },
  Prompt: { label: 'Prompt', icon: <DescriptionOutlined sx={TabIconSx} /> },
  Transcriber: { label: 'Transcriber and Voice', icon: <RecordVoiceOverOutlined sx={TabIconSx} /> },
  Configurations: { label: 'Configurations', icon: <SettingsOutlined sx={TabIconSx} /> },
  Actions: { label: 'Actions', icon: <CheckCircleOutline sx={TabIconSx} /> },
};

const AssistantForm = () => {
  const [token, setToken] = useState('');
  const [assistantId] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const navigate = useNavigate();
  const { company_id } = useParams();

  const validateAssistantData = () => {
    const assistant = JSON.parse(localStorage.getItem("assistant"));
    if (!assistant) return true;
    const requiredFields = ["Name", "Category", "Description", "Type", "Prompt"];
    for (const field of requiredFields) {
      if (!assistant[field] || assistant[field].toString().trim() === "") {
        return true;
      }
    }
    return false;
  };

  const getToken = async () => {
    const userData = JSON.parse(localStorage.getItem("User"));
    setUserRole(userData?.Role || "");
    setToken(userData?.Token);
  };

  useEffect(() => {
    getToken();
    setError(validateAssistantData());
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setError(validateAssistantData());
    }, 1000);
    return () => clearInterval(interval);
  }, [currentTab]);

  const handleTabChange = (newValue) => setCurrentTab(newValue);

  const handleSave = async () => {
    try {
      const assistant = JSON.parse(localStorage.getItem("assistant"));
      const logoData = JSON.parse(localStorage.getItem("Logo"));
      const knowledgeBaseData = JSON.parse(localStorage.getItem("KnowledgeBase"));

      const formData = new FormData();
      for (const [key, value] of Object.entries(assistant)) {
        formData.append(key, value);
      }

      const appendBase64File = (data, key) => {
        if (data) {
          const byteString = atob(data.content.split(',')[1]);
          const mimeString = data.content.split(',')[0].split(':')[1].split(';')[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: mimeString });
          formData.append(key, new File([blob], data.name, { type: data.type }));
        }
      };

      appendBase64File(logoData, "logo");
      if (knowledgeBaseData) {
        formData.append("knowledgeBaseName", knowledgeBaseData.name);
        formData.append("knowledgeBaseSize", knowledgeBaseData.size);
        formData.append("knowledgeBaseType", knowledgeBaseData.type);
        formData.append("knowledgeBaseUrl", knowledgeBaseData.url);
      }else if (assistant?.knowledgeBaseUrl == null) {
        formData.delete("knowledgeBaseName");
        formData.delete("knowledgeBaseSize");
        formData.delete("knowledgeBaseType");
        formData.delete("knowledgeBaseUrl");
      }

      setLoading(true);
      const response = await api.post("/v1/assistant/create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
      });

      toast.success(response.data.message);

      const actions = localStorage.getItem('actions');
      if (actions) {
        const parsedActions = JSON.parse(actions);
        for (const action of parsedActions) {
          if (!action._id) {
            action.Assistant = response.data.assistant._id;
            try {
              await api.post("/v1/actions/create-action", action, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              });
            } catch (actionError) {
              toast.error(actionError?.response?.data?.details);
            }
          }
        }
      }

      navigate(userRole === "SuperAdmin" ? "/assistants" : `/company/${company_id}/assistants`);
    } catch (error) {
      toast.error(error?.response?.data?.details);
    } finally {
      setLoading(false);
    }
  };

  const tabComponents = [
    <InitialDetails assistantId={assistantId} />,
    <NewPrompt assistantId={assistantId} />,
    <TranscriberAndAudio assistantId={assistantId} />,
    <AdditionalConfigurations assistantId={assistantId} />,
    <NewActions assistantId={assistantId} />,
  ];

  return (
    <Box sx={{ width: '100%', padding: '24px' }}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: '0.5rem' }}>
        <Box sx={{ display: "flex", backgroundColor: "#FFFFFF", border: '1px solid #E5E7EB', borderRadius: "12px", px:"0.5rem", py:"0.5rem", gap: "0.5rem", mt:"0.5rem", mb:"0.5rem" }}>
          {Object.values(tabsConfig).map((tab, index) => (
            <Typography
              key={index}
              onClick={() => handleTabChange(index)}
              sx={{
                cursor: "pointer",
                padding: "0.5rem 1rem",
                borderRadius: currentTab === index ? "4px" : "8px",
                fontWeight: 500,
                backgroundColor: currentTab === index ? "#02042D" : "transparent",
                color: currentTab === index ? "white" : "black",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                '&:hover': {
                  backgroundColor: currentTab === index ? "#02042D" : "#EDEDED",
                  color: currentTab === index ? "white" : "black",
                  borderRadius: "4px",
                }
              }}
            >
              {tab.icon}
              {tab.label}
            </Typography>
          ))}
        </Box>

        <TooltipHover title="Please fill out all required fields first" show={error}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={error || loading}
            sx={PublishButtonSx}
            startIcon={loading && <CircularProgress size={18} sx={{ color: "#fff" }} />}
          >
            {loading ? "Creating..." : "Create Assistant"}
          </Button>
        </TooltipHover>
      </Box>

      {tabComponents[currentTab]}
    </Box>
  );
};

export default AssistantForm;
